import { useState } from 'react';
import { useUploadDocument } from '../hooks/useUploadDocument';
import { useAppSelector } from '../hooks/hooks';
import { Alert, Button, Modal, Form, Spinner } from 'react-bootstrap';

interface Props {
  supplierId: string;
  show: boolean;
  onClose: () => void;
  onUploaded?: (documentId: string) => void;
  stackedOverModal?: boolean;
}

export function SupplierDocumentUploader({
  supplierId,
  show,
  onClose,
  onUploaded,
  stackedOverModal = false,
}: Props) {
  const { uploadDocument, uploading, error } = useUploadDocument();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { user } = useAppSelector((state) => state.auth);

  async function handleUpload() {
    if (!selectedFile || !user) return;
    const result = await uploadDocument(selectedFile, supplierId, user.id);
    if (result && onUploaded) {
      onUploaded(result.documentId);
    }
    setSelectedFile(null);
    onClose(); // close modal after success
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className={stackedOverModal ? 'stacked-child-modal' : undefined}
      backdropClassName={
        stackedOverModal ? 'stacked-child-modal-backdrop' : undefined
      }
    >
      <Modal.Header closeButton>
        <Modal.Title>Upload Supplier Document</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="formFile">
          <Form.Label>Select a document</Form.Label>
          <p>Supported files: .pdf,.doc,.docx,.xls,.xlsx</p>
          <Form.Control
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              // @ts-expect-error ts(2339)
              if (e.target.files?.[0]) setSelectedFile(e.target.files[0]);
            }}
          />
        </Form.Group>

        {selectedFile && (
          <p className="mt-2 text-muted">Selected: {selectedFile.name}</p>
        )}

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!selectedFile || uploading}
          onClick={handleUpload}
        >
          {uploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading...
            </>
          ) : (
            'Upload'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
